<template>
  <div class="text-center">
    <v-dialog v-model="bookingProfileDialog.visibility" max-width="620" persistent scrollable>
      <v-card class="pa-0" flat>
        <!-- Header -->
        <v-toolbar dark dense color="rgb(47, 207, 156)">
          <v-btn icon disabled></v-btn>
          <v-spacer></v-spacer>
          <v-toolbar-title>{{ isEdit ? 'Edit Booking Profile' : 'New Booking Profile' }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-6">
          <v-row no-gutters>
            <!-- Name -->
            <v-col cols="12" class="pb-4">
              <v-text-field v-model="form.name" outlined dense hide-details label="Profile Name"></v-text-field>
            </v-col>

            <!-- Target Queue -->
            <v-col cols="12" sm="6" class="pb-4 pr-sm-2">
              <v-select
                v-model="form.service_type_id"
                :items="bookingQueues"
                :item-text="queue => serviceTypeDisplay(queue.service_type_id)"
                item-value="service_type_id"
                outlined
                dense
                hide-details="auto"
                label="Target Queue (Online Booking)"
                hint="Reserved queue the imported bookings are routed to"
                persistent-hint
              ></v-select>
            </v-col>

            <!-- Fallback Queue -->
            <v-col cols="12" sm="6" class="pb-4 pl-sm-2">
              <v-select
                v-model="form.fallback_service_type_id"
                :items="walkInQueues"
                :item-text="queue => serviceTypeDisplay(queue.service_type_id)"
                item-value="service_type_id"
                outlined
                dense
                hide-details="auto"
                label="Fallback Queue (Walk-in)"
                hint="Used when the customer arrives outside the check-in window"
                persistent-hint
              ></v-select>
            </v-col>

            <!-- Source CS Center -->
            <v-col cols="12" sm="6" class="pb-4 pr-sm-2">
              <v-text-field
                v-model="form.source_cs_center"
                outlined
                dense
                hide-details="auto"
                label="CS Center (Import Value)"
                hint="Value of the CS Center column this profile matches"
                persistent-hint
              ></v-text-field>
            </v-col>

            <!-- Threshold -->
            <v-col cols="12" sm="6" class="pb-4 pl-sm-2">
              <v-text-field
                v-model="form.threshold_minutes"
                type="number"
                min="0"
                max="480"
                outlined
                dense
                hide-details="auto"
                label="Early / Late Threshold (Mins)"
                hint="Default 30 minutes"
                persistent-hint
              ></v-text-field>
            </v-col>

            <!-- Derived check-in window -->
            <v-col cols="12" class="pb-2">
              <v-alert dense text type="info" class="text-body-2 mb-0">{{ windowHint }}</v-alert>
            </v-col>

            <!-- Flags -->
            <v-col cols="12" sm="6">
              <v-switch v-model="form.is_default" dense hide-details class="mt-0" label="Default profile" hint="Rows with an unmatched CS Center fall back here" persistent-hint></v-switch>
            </v-col>
            <v-col cols="12" sm="6">
              <v-switch v-model="form.active" dense hide-details class="mt-0" label="Active" hint="Inactive profiles are skipped during import" persistent-hint></v-switch>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-6 pb-6">
          <v-btn block depressed color="primary" :disabled="saving" @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex'
import bookingDisplay from '@/mixins/bookingDisplay'
import { numericFieldValue } from '@/utils/form'

const DEFAULT_THRESHOLD_MINUTES = 30
// Every online booking slot is 30 minutes long (End Time = Start Time + 30), so the
// late side of the check-in window is measured from the end of the slot.
const SLOT_MINUTES = 30
const MAX_THRESHOLD_MINUTES = 480

function emptyForm() {
  return {
    booking_import_profile_id: null,
    name: null,
    service_type_id: null,
    fallback_service_type_id: null,
    source_cs_center: null,
    threshold_minutes: DEFAULT_THRESHOLD_MINUTES,
    is_default: false,
    active: true
  }
}

export default {
  name: 'bookingProfileDialog',
  mixins: [bookingDisplay],
  data() {
    return {
      form: emptyForm(),
      saving: false
    }
  },
  computed: {
    ...mapState(['bookingProfileDialog', 'bookingProfiles', 'general']),
    ...mapGetters(['hasOtherDefaultProfile']),
    isEdit() {
      return !!this.form.booking_import_profile_id
    },
    bookingQueues() {
      return this.general.queues.filter(queue => Number(queue.booking_enabled) === 1)
    },
    walkInQueues() {
      return this.general.queues.filter(queue => Number(queue.booking_enabled) !== 1)
    },
    otherProfiles() {
      return this.bookingProfiles.items.filter(profile => profile.booking_import_profile_id !== this.form.booking_import_profile_id)
    },
    threshold() {
      return numericFieldValue(this.form.threshold_minutes)
    },
    thresholdInRange() {
      return Number.isInteger(this.threshold) && this.threshold >= 0 && this.threshold <= MAX_THRESHOLD_MINUTES
    },
    // Spelled out so business users never have to work out the "+30" themselves:
    // they enter one threshold T and see the resulting window.
    windowHint() {
      if (!this.thresholdInRange) return `Enter a threshold between 0 and ${MAX_THRESHOLD_MINUTES} minutes.`
      const threshold = this.threshold
      const start = 14 * 60 + 30
      return `Check-in window = ${threshold} mins before the booked start time until ${threshold} mins after it ends. Example: a 14:30 - 15:00 booking allows check-in from ${this.formatMinutes(
        start - threshold
      )} to ${this.formatMinutes(start + SLOT_MINUTES + threshold)}.`
    }
  },
  methods: {
    ...mapActions(['saveBookingProfile']),
    ...mapMutations(['SET_BOOKING_PROFILE_DIALOG_VISIBILITY', 'SET_SNACKBAR']),
    formatMinutes(minutes) {
      const wrapped = ((minutes % 1440) + 1440) % 1440
      return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`
    },
    error(text) {
      this.SET_SNACKBAR({ status: true, color: 'error', text, icon: 'close', timeout: '4000' })
    },
    // Mirrors the backend validation (shared spec §3.4) so the user gets the message
    // before the round trip; the backend stays the authority and its message wins.
    validate() {
      const name = (this.form.name || '').trim()
      if (!name) return 'Profile name is required.'
      if (
        this.otherProfiles.some(
          profile =>
            String(profile.name)
              .trim()
              .toLowerCase() === name.toLowerCase()
        )
      ) {
        return `Profile name "${name}" is already used.`
      }

      if (!this.form.service_type_id) return 'Target queue is required.'
      if (!this.bookingQueues.some(queue => queue.service_type_id === this.form.service_type_id)) {
        return 'Target queue must be a booking-enabled queue.'
      }

      if (!this.form.fallback_service_type_id) return 'Fallback queue is required.'
      if (!this.walkInQueues.some(queue => queue.service_type_id === this.form.fallback_service_type_id)) {
        return 'Fallback queue must be a walk-in (non-booking) queue.'
      }

      if (!this.thresholdInRange) return `Threshold must be a whole number of minutes between 0 and ${MAX_THRESHOLD_MINUTES}.`

      const cs_center = (this.form.source_cs_center || '').trim()
      if (cs_center && this.form.active) {
        const clash = this.otherProfiles.find(profile => Number(profile.active) === 1 && String(profile.source_cs_center || '').trim() === cs_center)
        if (clash) return `CS Center "${cs_center}" is already mapped by the active profile "${clash.name}".`
      }

      // At least one default profile has to survive, otherwise unmatched CS Center
      // values would have nowhere to go.
      const wasDefault = this.isEdit && Number((this.bookingProfileDialog.selected_profile || {}).is_default) === 1
      if (wasDefault && !this.form.is_default && !this.hasOtherDefaultProfile(this.form.booking_import_profile_id)) {
        return 'At least one profile must stay the default. Set another profile as default first.'
      }
      if (this.form.is_default && !this.form.active) {
        return 'The default profile must stay active.'
      }

      return null
    },
    async save() {
      const message = this.validate()
      if (message) return this.error(message)

      const cs_center = (this.form.source_cs_center || '').trim()
      const params = {
        name: (this.form.name || '').trim(),
        service_type_id: this.form.service_type_id,
        fallback_service_type_id: this.form.fallback_service_type_id,
        source_cs_center: cs_center || null,
        threshold_minutes: this.threshold,
        is_default: this.form.is_default ? 1 : 0,
        active: this.form.active ? 1 : 0
      }
      if (this.isEdit) params.booking_import_profile_id = this.form.booking_import_profile_id

      this.saving = true
      // The dialog session this save belongs to. The close button stays enabled while saving, so
      // by the time the response lands the operator may have closed this dialog and reopened it
      // for another profile — closing THAT one would discard their edits. The list refresh still
      // happens either way: `saveBookingProfile` dispatches it, and it is not a dialog decision.
      const session = this.bookingProfileDialog.session
      try {
        if (await this.saveBookingProfile({ params })) {
          if (this.bookingProfileDialog.session === session) this.close()
        }
      } finally {
        this.saving = false
      }
    },
    close() {
      this.SET_BOOKING_PROFILE_DIALOG_VISIBILITY(false)
    },
    load() {
      const profile = this.bookingProfileDialog.selected_profile
      if (!profile) {
        this.form = emptyForm()
        return
      }
      this.form = {
        booking_import_profile_id: profile.booking_import_profile_id,
        name: profile.name,
        service_type_id: profile.service_type_id,
        fallback_service_type_id: profile.fallback_service_type_id,
        source_cs_center: profile.source_cs_center,
        threshold_minutes: profile.threshold_minutes == null ? DEFAULT_THRESHOLD_MINUTES : profile.threshold_minutes,
        is_default: Number(profile.is_default) === 1,
        active: Number(profile.active) === 1
      }
    }
  },
  watch: {
    'bookingProfileDialog.visibility'(visible) {
      if (visible) this.load()
    }
  }
}
</script>
